import { Navigate, useParams } from 'react-router-dom';
import { Tag } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { useItemKinds } from '@/features/inventory/hooks';
import { ItemsPage } from '@/pages/inventory/ItemsPage';

/**
 * Per-kind variant of the items grid, mounted at
 * `/catalog/items/kind/:kindSlug`. Resolves the `:kindSlug` URL
 * segment against the live kinds catalog and then delegates to
 * `ItemsPage` with that slug as a prop — `ItemsPage` handles the
 * server-side filter, hides the now-redundant Kind column / faceted
 * filter, swaps the header copy, and primes the "Add item" dialog
 * with the matching kind.
 *
 * Three terminal states while the catalog loads / resolves:
 *
 *   * `pending` — show a skeleton placeholder so the page chrome
 *     doesn't flash a misleading "not found" while the catalog
 *     query is in flight.
 *   * `not found` — the URL slug doesn't match any kind row (typo,
 *     deleted kind, etc.). Render a friendly empty state with a
 *     link back to the unfiltered items grid instead of a 404.
 *   * `missing slug` — defensive guard for a malformed URL; bounce
 *     back to `/catalog/items` so the user never sees a blank page.
 */
export function KindItemsPage() {
  const { kindSlug } = useParams<{ kindSlug: string }>();
  const { data: kinds, isLoading } = useItemKinds();

  if (!kindSlug) {
    return <Navigate to="/catalog/items" replace />;
  }

  if (isLoading || !kinds) {
    return (
      <div className="space-y-6">
        <PageHeader title="Items" />
        <Card>
          <CardContent className="space-y-3 pt-6">
            <Skeleton className="h-8 w-1/3" />
            <Skeleton className="h-64 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  const kindRow = kinds.find((k) => k.slug === kindSlug);
  if (!kindRow) {
    return (
      <div className="space-y-6">
        <PageHeader title="Items" />
        <Card>
          <CardContent className="pt-6">
            <EmptyState
              icon={Tag}
              title="That kind isn't in your catalog"
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  return <ItemsPage kindSlug={kindRow.slug} />;
}
